/**
 * Button — the Atlas action control. Behaviour and the disabled contract come
 * from Base UI's Button.
 *
 * The API follows the legacy Button so a migration is mostly a rename:
 * - `config` holds the look — the Figma `Type`, `Shape` and `Danger`
 *   properties plus the icon — as the legacy `config={{color, type}}` and
 *   `withIcon` did. Size, loading, disabled and fullWidth stay plain props.
 * - `as` picks the element: `'button'` (default, Base UI's Button), `'anchor'`
 *   or `'link'` (`next/link`). The last two render the real element rather
 *   than going through Base UI's `render`, which would stamp `role="button"`
 *   on them: navigation stays a link to assistive tech, as on the legacy
 *   Button. Disabled or loading, they get `aria-disabled`, leave the tab order
 *   and swallow the click.
 * - Top-level `type` is the native attribute again, defaulting to "button".
 * - `ariaDisabled` paints the disabled state but keeps focus and the click,
 *   as the legacy prop does; the stylesheet's disabled rule matches
 *   `[aria-disabled='true']` as well as `:disabled`.
 *
 * Migrating from the legacy Button (`src/components/atoms/Button`), the
 * pairs counted from the apps as of this writing:
 * - `config={{color: 'blue', type: 'primary'}}` → no config (primary default)
 * - `config={{color: 'blue', type: 'secondary'}}` → `config={{type: 'secondary'}}`
 * - `config={{color: 'blue', type: 'ghost'}}` → `config={{type: 'secondary'}}`
 * - `config={{color: 'blue', type: 'inline' | 'tertiary'}}` →
 *   `config={{type: 'link'}}` (the transparent, coloured-text style)
 * - `config={{color: 'red', type: T}}` → the same mapping plus `danger: true`
 * - `white`, `blueDark`, `green` have no Atlas equivalent yet; map the type
 *   and confirm with design.
 * - A legacy Button always becomes this Button, never IconButton: icon-only
 *   is `config={{type: 'link', shape: 'square', icon}}` plus `aria-label`.
 * - `size`: `small` / `medium` / `large` → `sm` / `md` / `lg`; `micro` has no
 *   v2 step, so `sm` is the nearest.
 * - `withIcon={{iconSrc, iconPosition}}` → `config.icon={{name, position}}`;
 *   `name` is a Font Awesome class string, so an SVG `iconSrc` has no direct
 *   equivalent. `fitParent` → `fullWidth`; `as` and `ariaDisabled` carry over
 *   unchanged; `withAnimation` has no counterpart.
 *
 * `config.type` re-points the `--btn-*` custom properties and `danger`
 * overrides the same three, so the stylesheet stays three type blocks plus one
 * override instead of a type × danger matrix.
 *
 * Figma's `State` is not a prop: Normal, Hover or press, Focused and Disabled
 * are `:hover`, `:active`, `:focus-visible` and `disabled`, and `loading` adds
 * `aria-busy`. Hover and press share one style, as they do in the panel.
 *
 * `config.icon` is `{name, position}`: `name` is a Font Awesome class string
 * passed straight to Icon; `position` (default `'left'`) picks the
 * `data-icon="inline-start"` / `"inline-end"` slot the stylesheet styles. A
 * caller composing its own node keeps using those `data-icon` children
 * directly; both paths render into the same label and coexist. The square
 * shape is the icon, so `icon` is required there and `position` is ignored.
 *
 * No hooks: `loading` is a rendered slot, not state, and keyboard-only focus is
 * the `:focus-visible` pseudo-class (Base UI's Button exposes `data-disabled`
 * and nothing else — see `ButtonDataAttributes`).
 *
 * Stylesheet notes, since the SCSS carries no comments:
 * - The accent trio is the only thing `danger` swaps; each type block decides
 *   which of the three it reads, and the danger block is declared after the
 *   type blocks so it wins on source order at the same specificity.
 * - `--btn-accent-deep` is the deeper solid fill for the filled type,
 *   `--btn-accent-tint` the wash for the flat ones. Atlas exposes a single
 *   error surface, so a danger primary reuses its own fill on hover and reads
 *   the change from the pointer alone — point `--btn-accent-deep` at a darker
 *   step the moment the export gains one.
 * - Hover and press share one rule, as the Figma panel collapses them.
 * - The focus ring is one colour for every type, danger included, so a keyboard
 *   user learns a single indicator. Atlas has no 2px border-size role (default
 *   1px, big 1.5px), so that width is a literal until one exists; the 2px gap
 *   outside the box is Spacing 1.
 * - The box is `--button-height`, `--button-font-size` and
 *   `--button-line-height`, declared once on the block with the md defaults; a
 *   size modifier repoints those three values and nothing else. The Figma
 *   heights are 40 / 48 / 56 and no `--size-*` role holds 40 or 48, so those
 *   are literals; the Atlas `--button-padding-x` / `-y` hooks stay as they are.
 *   The square shape takes its box from that height and 1:1 gives the width.
 * - The box holds its shape inside any page. Legacy Button put its text
 *   straight in the element, so app stylesheets grew bare `span {…}` /
 *   `button {…}` rules that now reach this Button's label and icon. Every
 *   property that defines the box (height, padding, border, radius, surface,
 *   colour, font, line-height) sits on a triple class, `.button.button.button`
 *   (0,3,0), and the label, icon and spinner are reset at (0,4,0). That beats
 *   `.wrapper span` (0,1,1) and `.a .b span` (0,2,1) without `!important`.
 *   Ceiling: a caller chain of three classes plus an element still wins; the
 *   upgrade path is to remove that caller rule, not to raise this further.
 *   Because of that armour, states and modifiers change `--btn-*` custom
 *   properties rather than the properties themselves, and a composing
 *   molecule tunes the box the same way — ActionCard sets `--btn-height:
 *   auto` (declared on the block as `--button-height`) to grow with its content. Layout that molecules compose (label
 *   display, width, gap, white-space) stays at single-class specificity on
 *   purpose. Text alignment is the `--button-text-align` hook (`center`
 *   on the block), set the way ActionCard sets its padding: a same-specificity
 *   `text-align` on the composing class would otherwise win or lose by
 *   stylesheet order.
 * - Loading composes the Spinner atom at sm (16px) for every button size, as
 *   the Figma Loading variants do, so the sweep and track colours are the
 *   atom's own. It is `role="presentation"` and `aria-hidden` because the
 *   button already reports the busy state through `aria-busy`; a nested status
 *   region would announce it a second time. It sits absolutely over the title,
 *   anchored with `inset: 0` and `margin: auto`, and the title keeps its box so
 *   the button does not resize. The title hides with `opacity`, not
 *   `visibility`: a `visibility: hidden` label leaves the accessibility tree,
 *   and it is the button's only name.
 * - Loading sets native `disabled`, so it paints the disabled surface; the
 *   square shape swaps that for Surface/Subtle, as Figma draws it. That rule
 *   sits after `:disabled` at the same specificity and before the link type's
 *   disabled reset, so a loading link square stays transparent.
 * - The link type has no stroke in Figma, so its border width is 0 rather
 *   than a transparent 1px, which is what makes it 2px narrower than the
 *   filled types. Its height still comes from `min-height`, and the focus
 *   indicator is an outline, so neither depends on the border.
 *
 * The hook defaults sit on `:where()`, so a caller's value on its own class always wins.
 */

import type {FC, MouseEvent} from 'react';
import {Button as ButtonPrimitive} from '@base-ui/react/button';
import cn from 'classnames';
import Link from 'next/link';

import {Icon} from '../Icon';
import {Spinner} from '../Spinner';

import type {
  ButtonAsAnchorProps,
  ButtonAsLinkProps,
  ButtonConfig,
  ButtonContentProps,
  ButtonProps,
} from './types';
import {buttonRootProps} from './utils';

import styles from './styles.module.scss';

/** A disabled or loading anchor: announced as such, out of the tab order, no navigation. */
const inertAnchor = {
  'aria-disabled': true,
  tabIndex: -1,
  onClick: (event: MouseEvent<HTMLAnchorElement>) => event.preventDefault(),
};

/** The spinner (while loading) over the label, with the config icon in its slot. */
const ButtonContent: FC<ButtonContentProps> = ({loading, icon, children}) => {
  const position = icon?.position ?? 'left';

  return (
    <>
      {loading ? (
        <Spinner
          size="sm"
          role="presentation"
          aria-hidden
          className={styles.button__spinner}
        />
      ) : null}
      <span
        data-slot="button-label"
        className={cn(styles.button__label, {
          [styles.button__label__modHidden]: loading,
        })}>
        {icon && position === 'left' ? (
          <Icon name={icon.name} data-icon="inline-start" />
        ) : null}
        {children}
        {icon && position === 'right' ? (
          <Icon name={icon.name} data-icon="inline-end" />
        ) : null}
      </span>
    </>
  );
};

const Button: FC<ButtonProps> = ({
  as = 'button',
  type = 'button',
  config,
  className,
  size = 'md',
  loading = false,
  fullWidth = false,
  disabled = false,
  ariaDisabled = false,
  children,
  ...props
}) => {
  const {
    type: look = 'primary',
    shape = 'standard',
    danger = false,
    icon,
  }: ButtonConfig = config ?? {};
  const inert = disabled || loading;
  const shared = buttonRootProps({
    look,
    shape,
    danger,
    size,
    loading,
    ariaDisabled,
    fullWidth,
    className,
  });
  const content = (
    <ButtonContent loading={loading} icon={icon}>
      {children}
    </ButtonContent>
  );

  // `as` narrows the rest, but TS loses that across the destructure, so each
  // branch restates it. Caller props spread last so a `data-slot` override
  // (ActionCard's, for one) wins.
  if (as === 'link') {
    const linkProps = props as Omit<ButtonAsLinkProps, 'as'>;

    return (
      <Link {...shared} {...linkProps} {...(inert && inertAnchor)}>
        {content}
      </Link>
    );
  }

  if (as === 'anchor') {
    return (
      <a
        {...shared}
        {...(props as Omit<ButtonAsAnchorProps, 'as'>)}
        {...(inert && inertAnchor)}>
        {content}
      </a>
    );
  }

  return (
    <ButtonPrimitive
      {...shared}
      type={type}
      disabled={inert}
      {...(props as ButtonPrimitive.Props)}>
      {content}
    </ButtonPrimitive>
  );
};

export {Button};
